import { Stack } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { CopyableCode } from '@/components/shared/copyableCode';

interface BigQueryActionRequiredDialogProps {
  resources: InventoryResource[];
  onClose: () => void;
}

export const BigQueryActionRequiredDialog = ({
  resources,
  onClose,
}: BigQueryActionRequiredDialogProps) => {
  const script = generateEnableChangeHistoryScript(resources);

  return (
    <MainDialog
      isOpen={true}
      isLoading={false}
      header={{
        title: 'Enable change history',
        subtitle:
          'One or more tables in this dataset have change history disabled. Copy and run the following SQL script in the BigQuery console to enable change history for all tables in the dataset.',
        onClose,
      }}
      footer={{
        primaryButton: {
          text: 'Close',
          onClick: onClose,
          variant: 'outlined',
          'data-testid': 'bigquery-action-required-dialog-close-button',
        },
      }}
      sx={{
        width: '50%',
        minWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'hidden',
      }}
    >
      <Stack className='px-[24px] py-0'>
        <CopyableCode
          code={script}
          title='enable_change_history.sql'
          codeClassName='overflow-y-auto max-h-[40vh]'
        />
      </Stack>
    </MainDialog>
  );
};

const generateEnableChangeHistoryScript = (
  resources: InventoryResource[]
): string => {
  const allStatements: string[] = [];

  for (const resource of resources) {
    const bigQueryProps = resource.resourceProperties?.gcpBigQuery;
    const gcpProjectId = resource.providerAccountId;
    const datasetId = bigQueryProps?.datasetId;
    const tableNames = (bigQueryProps?.tables ?? [])
      .map((t) => t.name)
      .filter((name): name is string => !!name);

    if (!gcpProjectId || !datasetId) continue;

    for (const tableName of tableNames) {
      allStatements.push(
        `ALTER TABLE \`${gcpProjectId}.${datasetId}.${tableName}\`\nSET OPTIONS (\n  enable_change_history = TRUE\n);`
      );
    }
  }

  if (allStatements.length === 0) {
    return 'No tables found in dataset';
  }

  return allStatements.join('\n\n');
};
